import { useEffect, useRef, useState } from "react";
import { Marker, Popup, Circle } from "react-leaflet";
import { useMap } from "react-leaflet/hooks";
import PointOfSalePopup from "./point-of-sale-popup/point-of-sale-popup";
import { WARSAW_COORDS } from "../points-of-sale-map";
import Leaflet from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { BlueCircle } from "./blue-circle/blue-circle";

function PointsOfSaleCustomMap({ pointsOfSale, setViewBounds }) {
  const map = useMap();

  const updateBoundOnlyOnUserAction = useRef(true);
  const userMovedRef = useRef(false);
  const prevBoundsRef = useRef(map.getBounds());
  const [userPosition, setUserPosition] = useState(null);

  useEffect(() => {
    const onDragStart = () => {
      map.closePopup();
      userMovedRef.current = true;
    };

    const onZoomStart = (e) => {
      map.closePopup();
      userMovedRef.current = true;
    };

    const onMouseDown = () => {
      map.closePopup();
    };

    map.on("dragstart", onDragStart);
    map.on("zoomstart", onZoomStart);
    map.on("mousedown", onMouseDown);

    return () => {
      map.off("dragstart", onDragStart);
      map.off("zoomstart", onZoomStart);
      map.off("mousedown", onMouseDown);
    };
  }, [map]);

  useEffect(() => {
    const handleMoveEnd = () => {
      const newBounds = map.getBounds();

      const boundsChanged = !prevBoundsRef.current.equals(newBounds);
      const userMoved = userMovedRef.current;
      if (boundsChanged && (!updateBoundOnlyOnUserAction.current || userMoved)) {
        //info workaround, coz moveend event is called when marker popup animation is displayed
        prevBoundsRef.current = newBounds;
        updateBoundOnlyOnUserAction.current = true;
        setViewBounds(newBounds);
      }

      userMovedRef.current = false;
    };

    map.on("moveend", handleMoveEnd);
    return () => {
      map.off("moveend", handleMoveEnd);
    };
  }, [map, setViewBounds]);

  const updatePointsAndView = (position) => {
    const userCoords = position ? [position.coords.latitude, position.coords.longitude] : WARSAW_COORDS;
    const nearPointsView = getNearPointsBound(userCoords);
    updateBoundOnlyOnUserAction.current = false;
    map.fitBounds(nearPointsView);
    setViewBounds(map.getBounds());
  };

  useEffect(() => {
    updatePointsAndView();
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserPosition([position.coords.latitude, position.coords.longitude]);
        updatePointsAndView(position);
      },
      (error) => {
        console.error("Error getting location: ", error);
      }
    );
  }, [map]);

  const CustomDivIcon = new Leaflet.DivIcon({
    html: renderToStaticMarkup(<BlueCircle />),
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  return (
    <>
      {userPosition && (
        <>
          <Circle center={userPosition} pathOptions={{ fillColor: "blue" }} radius={50} />
          <Marker key="marker_user" position={userPosition} icon={CustomDivIcon}></Marker>)
        </>
      )}
      {pointsOfSale.map((point, idx) => (
        <Marker key={"marker_" + idx} position={point.position}>
          <Popup>
            <PointOfSalePopup pointOfSale={point} userPosition={userPosition} />
          </Popup>
        </Marker>
      ))}
    </>
  );
}

/**Gets bounds for points ~10 km in all directions from the position */
function getNearPointsBound(position) {
  const latDiff = 5 / 111;
  const lngDiff = 5 / (111 * Math.cos((position[0] * Math.PI) / 180));

  const bounds = [
    [position[0] - latDiff, position[1] - lngDiff],
    [position[0] + latDiff, position[1] + lngDiff],
  ];

  return bounds;
}

function getBoundsFromPoints(points) {
  //todo tbd whether remove in advanced version
  let max_x = 0;
  let max_y = 0;
  let min_x = 0;
  let min_y = 0;

  if (points && points.length > 0) {
    let maxing = points.map((p) => {
      const x = p[0];
      return x;
    });
    max_x = Math.max(...maxing);
    max_y = Math.max(...points.map((p) => p[1]));
    min_x = Math.min(...points.map((p) => p[0]));
    min_y = Math.min(...points.map((p) => p[1]));
  }

  return [
    [min_x, max_y],
    [max_x, min_y],
  ];
}

export default PointsOfSaleCustomMap;
